import { Link } from "react-router-dom";
import { Icon } from "@iconify/react";

export default function PhilosophySection() {
  return (
    <section className="py-12 sm:py-16 lg:py-[80px] overflow-hidden bg-white">
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6">
        {/* Top Section - Philosophy */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-[60px] items-center mb-[40px]">
          {/* Left - Content */}
          <div>
            {/* Badge */}
            <div className="flex items-center gap-2 mb-5">
              <div className="w-8 h-[2px] bg-[#1b667a]" />
              <span className="text-[12px] font-bold tracking-[2px] text-[#1b667a] uppercase">Our Philosophy</span>
            </div>

            {/* Title */}
            <h2 className="text-[42px] leading-[1.2] font-bold text-gray-900 mb-6
              max-[1199px]:text-[36px]
              max-[991px]:text-[30px]
              max-[575px]:text-[26px]">
              Engineering-first solutions that drive measurable{" "}
              <span className="text-transparent text-[#1b667a]">
                business growth.
              </span>
            </h2>

            {/* Description */}
            <p className="text-gray-500 text-[15px] leading-[1.8] mb-8 max-w-[480px]">
              We combine deep technical expertise with business understanding to build scalable, reliable, and future-ready solutions that create real impact.
            </p>

            {/* Button */}
            <Link to="/about">
              <button className="inline-flex items-center gap-2 bg-brand-teal text-white px-6 py-3 rounded-xl text-[14px] font-semibold hover:bg-[#155568] transition-all duration-300 hover:-translate-y-0.5 shadow-[0_4px_16px_rgba(27,102,122,0.3)]">
                Our Approach
                <Icon icon="mdi:arrow-top-right" className="w-4 h-4" />
              </button>
            </Link>
          </div>

          {/* Right - Image */}
          <div className="relative">
            <div className="relative rounded-2xl overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.12)]">
              <img src="/images/tech-startup/goal.jpeg" alt="Our Goal" className="w-full h-[260px] sm:h-[340px] lg:h-[400px] object-cover object-right" />
            </div>

            {/* Floating badge */}
            <div className="absolute -top-2 -right-2 sm:-top-3 sm:-right-3 bg-white rounded-xl p-2.5 sm:p-3 shadow-[0_8px_30px_rgba(0,0,0,0.1)] flex items-center gap-2 max-w-[180px]">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#1b667a] to-teal-400 flex items-center justify-center">
                <Icon icon="mdi:cube-outline" className="w-4 h-4 text-white" />
              </div>
              <div>
                <div className="text-[11px] font-bold text-gray-900">Building</div>
                <div className="text-[10px] text-gray-500">Scalable Solutions</div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Section - Goal Card */}
        <div className="relative rounded-3xl overflow-hidden bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 p-[50px] flex items-center justify-between gap-10
          max-[991px]:flex-col max-[991px]:p-[30px] max-[991px]:text-center">
          {/* Background decoration */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 right-0 w-[300px] h-[300px] bg-[#1b667a]/5 blur-3xl" />
            <div className="absolute bottom-0 left-0 w-[200px] h-[200px] bg-teal-500/5 blur-3xl" />
            {/* Wave lines */}
            <svg className="absolute bottom-0 left-0 w-full h-[60px] opacity-10" viewBox="0 0 1200 60" fill="none">
              <path d="M0 30 Q300 0 600 30 Q900 60 1200 30" stroke="#10B981" strokeWidth="1" fill="none" />
              <path d="M0 40 Q300 10 600 40 Q900 70 1200 40" stroke="#10B981" strokeWidth="0.5" fill="none" />
            </svg>
          </div>

          {/* Left - Target Icon */}
          <div className="hidden lg:flex w-[80px] h-[80px] rounded-full border border-[#1b667a]/30 items-center justify-center flex-shrink-0 relative">
            <div className="w-[60px] h-[60px] rounded-full border border-[#1b667a]/20 flex items-center justify-center">
              <Icon icon="mdi:target" className="w-7 h-7 text-emerald-400" />
            </div>
          </div>

          {/* Center - Content */}
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-3 max-[991px]:justify-center">
              <div className="w-6 h-[2px] bg-[#1b667a]" />
              <span className="text-[11px] font-bold tracking-[2px] text-emerald-400 uppercase">Our Goal</span>
            </div>
            <h3 className="text-[28px] leading-[1.3] font-bold text-white
              max-[1199px]:text-[24px]
              max-[991px]:text-[22px]">
              Bridge the gap between ambitious business strategies and scalable code.
            </h3>
          </div>

          {/* Right - Growth Icon */}
          <div className="hidden lg:flex w-[60px] h-[60px] rounded-xl bg-[#1b667a]/10 border border-[#1b667a]/20 items-center justify-center flex-shrink-0">
            <Icon icon="mdi:trending-up" className="w-7 h-7 text-emerald-400" />
          </div>
        </div>

        {/* Mission & Vision Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-5">
          {/* Mission Card */}
          <div className="bg-gradient-to-br from-blue-50/80 to-blue-100/40 rounded-2xl p-5 border border-blue-100/60 relative overflow-hidden">
            {/* Background circles */}
            <div className="absolute bottom-[-50px] left-[-50px] w-[200px] h-[200px] rounded-full border border-blue-200/30" />
            <div className="absolute bottom-[-25px] left-[-25px] w-[150px] h-[150px] rounded-full border border-blue-200/20" />

            {/* Icon + Badge */}
            <div className="flex items-center gap-3 mb-5">
              <div className="w-[48px] h-[48px] rounded-full bg-gradient-to-br from-blue-500 to-blue-400 flex items-center justify-center shadow-[0_4px_12px_rgba(59,130,246,0.25)]">
                <Icon icon="mdi:rocket-launch-outline" className="w-5 h-5 text-white" />
              </div>
              <div className="flex items-center gap-2">
                <div className="w-5 h-[2px] bg-blue-500" />
                <span className="text-[11px] font-bold tracking-[2px] text-blue-500 uppercase">Our Mission</span>
              </div>
            </div>

            {/* Title */}
            <h3 className="text-[28px] font-bold text-gray-900 mb-4 leading-tight max-w-[380px]">
              Delivering dependable engineering for ambitious teams.
            </h3>

            {/* Description */}
            <p className="text-[17px] text-gray-600 leading-[1.8] max-w-[420px] mb-6">
              We bypass traditional agency red tape to connect you directly with senior developers. Our mission is to architect secure, high-performance web, mobile, and AI solutions that solve your most complex operational bottlenecks.
            </p>

            {/* 3D Isometric Illustration */}
            <div className="flex justify-center overflow-hidden">
              <svg className="w-full max-w-[280px] h-auto" viewBox="0 0 200 140" fill="none" xmlns="http://www.w3.org/2000/svg">
                {/* Base platform */}
                <ellipse cx="100" cy="120" rx="80" ry="16" fill="url(#blueGrad1)" opacity="0.15" />
                {/* Bottom cube */}
                <path d="M70 100 L100 115 L130 100 L100 85 Z" fill="url(#blueGrad2)" opacity="0.4" />
                <path d="M70 100 L100 115 L100 95 L70 80 Z" fill="url(#blueGrad3)" opacity="0.5" />
                <path d="M130 100 L100 115 L100 95 L130 80 Z" fill="url(#blueGrad4)" opacity="0.6" />
                {/* Top cube */}
                <path d="M80 80 L110 95 L140 80 L110 65 Z" fill="url(#blueGrad2)" opacity="0.5" />
                <path d="M80 80 L110 95 L110 75 L80 60 Z" fill="url(#blueGrad3)" opacity="0.6" />
                <path d="M140 80 L110 95 L110 75 L140 60 Z" fill="url(#blueGrad4)" opacity="0.7" />
                {/* Small cube */}
                <path d="M90 60 L110 70 L130 60 L110 50 Z" fill="url(#blueGrad2)" opacity="0.6" />
                <path d="M90 60 L110 70 L110 58 L90 48 Z" fill="url(#blueGrad3)" opacity="0.7" />
                <path d="M130 60 L110 70 L110 58 L130 48 Z" fill="url(#blueGrad4)" opacity="0.8" />
                {/* Floating dots */}
                <circle cx="50" cy="70" r="3" fill="#3B82F6" opacity="0.3" />
                <circle cx="155" cy="55" r="2" fill="#60A5FA" opacity="0.4" />
                <circle cx="45" cy="95" r="2" fill="#93C5FD" opacity="0.3" />
                <defs>
                  <linearGradient id="blueGrad1" x1="20" y1="104" x2="180" y2="136">
                    <stop stopColor="#3B82F6" />
                    <stop offset="1" stopColor="#60A5FA" />
                  </linearGradient>
                  <linearGradient id="blueGrad2" x1="70" y1="60" x2="130" y2="115">
                    <stop stopColor="#60A5FA" />
                    <stop offset="1" stopColor="#3B82F6" />
                  </linearGradient>
                  <linearGradient id="blueGrad3" x1="70" y1="60" x2="100" y2="115">
                    <stop stopColor="#93C5FD" />
                    <stop offset="1" stopColor="#3B82F6" />
                  </linearGradient>
                  <linearGradient id="blueGrad4" x1="100" y1="60" x2="130" y2="115">
                    <stop stopColor="#3B82F6" />
                    <stop offset="1" stopColor="#2563EB" />
                  </linearGradient>
                </defs>
              </svg>
            </div>
          </div>

          {/* Vision Card */}
          <div className="bg-gradient-to-br from-emerald-50/80 to-green-100/40 rounded-2xl p-5 border border-emerald-100/60 relative overflow-hidden">
            {/* Background circles */}
            <div className="absolute bottom-[-50px] right-[-50px] w-[200px] h-[200px] rounded-full border border-emerald-200/30" />
            <div className="absolute bottom-[-25px] right-[-25px] w-[150px] h-[150px] rounded-full border border-emerald-200/20" />

            {/* Icon + Badge */}
            <div className="flex items-center gap-3 mb-5">
              <div className="w-[48px] h-[48px] rounded-full bg-gradient-to-br from-[#1b667a] to-green-400 flex items-center justify-center shadow-[0_4px_12px_rgba(16,185,129,0.25)]">
                <Icon icon="mdi:eye-outline" className="w-5 h-5 text-white" />
              </div>
              <div className="flex items-center gap-2">
                <div className="w-5 h-[2px] bg-[#1b667a]" />
                <span className="text-[11px] font-bold tracking-[2px] text-[#1b667a] uppercase">Our Vision</span>
              </div>
            </div>

            {/* Title */}
            <h3 className="text-[28px] font-bold text-gray-900 mb-4 leading-tight max-w-[380px]">
              Be Your Dedicated Technical Backbone
            </h3>

            {/* Description */}
            <p className="text-[17px] text-gray-600 leading-[1.8] max-w-[420px] mb-6">
              We envision a digital landscape where growing businesses never have to compromise on code quality. We strive to be the definitive engineering partner that enterprises rely on to build, scale, and future-proof their software ecosystems.
            </p>

            {/* 3D Stacked Layers Illustration */}
            <div className="flex justify-center overflow-hidden">
              <svg className="w-full max-w-[280px] h-auto" viewBox="0 0 200 140" fill="none" xmlns="http://www.w3.org/2000/svg">
                {/* Base platform */}
                <ellipse cx="100" cy="125" rx="70" ry="12" fill="url(#greenGrad1)" opacity="0.15" />
                {/* Bottom layer */}
                <path d="M40 110 L100 130 L160 110 L100 90 Z" fill="url(#greenGrad2)" opacity="0.3" />
                <path d="M40 110 L100 130 L100 120 L40 100 Z" fill="url(#greenGrad3)" opacity="0.4" />
                <path d="M160 110 L100 130 L100 120 L160 100 Z" fill="url(#greenGrad4)" opacity="0.5" />
                {/* Middle layer */}
                <path d="M50 95 L100 112 L150 95 L100 78 Z" fill="url(#greenGrad2)" opacity="0.4" />
                <path d="M50 95 L100 112 L100 104 L50 87 Z" fill="url(#greenGrad3)" opacity="0.5" />
                <path d="M150 95 L100 112 L100 104 L150 87 Z" fill="url(#greenGrad4)" opacity="0.6" />
                {/* Top layer */}
                <path d="M60 78 L100 93 L140 78 L100 63 Z" fill="url(#greenGrad2)" opacity="0.6" />
                <path d="M60 78 L100 93 L100 86 L60 71 Z" fill="url(#greenGrad3)" opacity="0.7" />
                <path d="M140 78 L100 93 L100 86 L140 71 Z" fill="url(#greenGrad4)" opacity="0.8" />
                {/* Floating dots */}
                <circle cx="45" cy="85" r="3" fill="#10B981" opacity="0.3" />
                <circle cx="160" cy="75" r="2" fill="#34D399" opacity="0.4" />
                <circle cx="50" cy="65" r="2" fill="#6EE7B7" opacity="0.3" />
                <defs>
                  <linearGradient id="greenGrad1" x1="30" y1="113" x2="170" y2="137">
                    <stop stopColor="#10B981" />
                    <stop offset="1" stopColor="#34D399" />
                  </linearGradient>
                  <linearGradient id="greenGrad2" x1="40" y1="63" x2="160" y2="130">
                    <stop stopColor="#34D399" />
                    <stop offset="1" stopColor="#10B981" />
                  </linearGradient>
                  <linearGradient id="greenGrad3" x1="40" y1="63" x2="100" y2="130">
                    <stop stopColor="#6EE7B7" />
                    <stop offset="1" stopColor="#10B981" />
                  </linearGradient>
                  <linearGradient id="greenGrad4" x1="100" y1="63" x2="160" y2="130">
                    <stop stopColor="#10B981" />
                    <stop offset="1" stopColor="#059669" />
                  </linearGradient>
                </defs>
              </svg>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
